import styles from './brand.module.css';
import type { SiteId } from '@goodbuild/rules/sites';

/**
 * The Progress Strip. Brand guidelines section 03, the signature device.
 *
 * Five ascending bars whose heights are review scores across released
 * versions, newest rightmost and always in the panel's accent. A studio that
 * ships real updates draws a staircase; one that does not draws a flat line.
 *
 * **The one rule: never draw a strip that does not represent real data.**
 *
 * *"Fabricating a rising staircase for a game that has not earned it turns the
 * platform's most honest device into its biggest lie, and it would be
 * noticed."*
 *
 * That rule is enforced here by the types rather than by a comment. There are
 * exactly four components, and none of them accepts an arbitrary list of bar
 * heights:
 *
 *   - `VersionStrip` takes scored versions and derives the heights. It cannot
 *     draw a shape that the scores did not produce.
 *   - `DownloadStrip` takes a completed count. Equal bars, no score implied.
 *   - `LogoStrip` is the fixed identity mark, at the proportions the
 *     guidelines specify. It is a logo, not a chart, and it is the only one
 *     with hard-coded heights.
 *   - `Loader` is that same mark with a light running through it. It takes no
 *     data at all, and it animates colour rather than height precisely so that
 *     a wait cannot draw a rising staircase nobody earned. `PageLoader` is the
 *     same component given the whole window, for the waits that happen before
 *     there is a page to put one inside.
 *
 * There is deliberately no `<Strip heights={[30, 50, 70]} />`. A component
 * that took heights directly is the one somebody would reach for to decorate a
 * marketing page, and the rule would be broken within a week.
 */

/**
 * The identity lockup's bar heights. Fixed, from the guidelines.
 *
 * Exported because the mark is now drawn in three places — this component,
 * `tools/brand-mark.mjs` for the raster and favicon set, and the ident film in
 * `apps/reel`, which animates each bar separately and so cannot use
 * `LogoStrip` as a black box. Exporting the numbers is what stops the third
 * one from becoming a fourth opinion about what the logo looks like.
 *
 * It is the heights only. There is still no component that accepts arbitrary
 * bar heights, and there must not be — see the rule above.
 */
export const LOGO_BARS = [30, 48, 66, 82, 100] as const;

export type StripSize = 'tiny' | 'sm' | 'md' | 'lg';

/* CSS module members are typed as possibly-undefined, so an unmatched class
   name degrades to no class rather than the literal string "undefined". */
const SIZE_CLASS: Record<StripSize, string> = {
  tiny: styles.stripTiny ?? '',
  sm: styles.stripSm ?? '',
  md: styles.stripMd ?? '',
  lg: styles.stripLg ?? '',
};

/**
 * The mark. Four or five bars at fixed heights, top bar in the panel's accent.
 *
 * Decorative in the sense that a logo is decorative — it stands for the
 * platform, not for any title's score. The heights never change, which is what
 * keeps it a mark rather than a chart somebody might read.
 */
export function LogoStrip({
  size = 'sm',
  bars = 5,
  onDark = false,
}: {
  size?: StripSize;
  bars?: 4 | 5;
  onDark?: boolean;
}): React.ReactElement {
  const heights = bars === 4 ? [30, 53, 76, 100] : [...LOGO_BARS];

  return (
    <span
      className={`${styles.strip} ${SIZE_CLASS[size]}`}
      aria-hidden="true"
      data-on-dark={onDark ? '' : undefined}
    >
      {heights.map((height, index) => (
        <i
          key={height}
          style={{ height: `${height}%` }}
          // The top bar is always the panel's accent, the three below it plain
          // type colour. It was Volt over Build, which put a lime-and-blue mark
          // in the navigation of a store whose palette contains neither.
          data-bar={
            index === heights.length - 1 ? 'current' : index >= heights.length - 3 ? 'past' : 'dim'
          }
        />
      ))}
    </span>
  );
}

/*
 * The running strip's own size classes, rather than `.stripSm` and friends.
 *
 * Those carry `gb-bar-grow` — the one-shot assemble the strip does when a page
 * paints — and a bar cannot run two animations on the same property. Sharing
 * them would have meant unpicking that with `nth-child` selectors of matching
 * specificity, which is a thing to get wrong every time either animation is
 * touched. CSS module members are typed as possibly-undefined, as above.
 */
const LOADER_SIZE: Record<Exclude<StripSize, 'tiny'>, string> = {
  sm: styles.loaderSm ?? '',
  md: styles.loaderMd ?? '',
  lg: styles.loaderLg ?? '',
};

/**
 * Waiting, drawn as the mark.
 *
 * **Every surface used to wait by printing `Loading…` into the empty state** —
 * the same dashed box a table with no rows gets — so *we are still asking* and
 * *there is nothing here* were the same picture for as long as the request
 * took. On a games platform, of all things.
 *
 * This is the logo with a light running up it, and then a beat, and then again.
 * The heights never move: section 03's one rule is that a strip must never draw
 * a shape its data did not produce, and a loader that animated bar heights
 * would hand every game a rising staircase for free while it waited. What moves
 * is the colour and a three-pixel lift, so what is on screen stays exactly the
 * identity mark.
 *
 * **It waits in the colours of whoever is reading.** The bars take
 * `--gb-accent`, which is Build in a studio's portal, Hot on a creator's board
 * and Solar on the store — so one component is right on all three surfaces and
 * nothing has to pass it a channel. `space` is only for a surface that has not
 * declared one, and it paints that surface's ground as well as its accent,
 * because `[data-gb-space]` carries both.
 *
 * The label is a real sentence rather than decoration: `role="status"` means a
 * screen reader hears it when it appears, and `Reading Steam` tells somebody
 * which of the two requests on the page they are waiting for.
 */
export function Loader({
  label = 'Loading',
  size = 'md',
  block = false,
  space,
}: {
  /** What is being waited for. Announced, so write it for a person. */
  label?: string;
  size?: Exclude<StripSize, 'tiny'>;
  /** Centred, with room around it: a page waiting rather than a control. */
  block?: boolean;
  /**
   * Whose colours, where the surface has not already said.
   *
   * Almost nothing passes this. Every `Page` and every `AuthScreen` already
   * carries `data-gb-space`, and the accent resolves from the nearest one — so
   * setting it again here would only be a second opinion about a question
   * already answered.
   */
  space?: SiteId;
}): React.ReactElement {
  const mark = (
    <span className={styles.loader} role="status" {...(space ? { 'data-gb-space': space } : {})}>
      <span className={`${styles.loaderStrip} ${LOADER_SIZE[size]}`} aria-hidden="true">
        {LOGO_BARS.map((height) => (
          <i key={height} style={{ height: `${height}%` }} />
        ))}
      </span>
      <span className={styles.loaderLabel}>{label}</span>
    </span>
  );

  if (!block) return mark;
  return <div className={styles.loaderBlock}>{mark}</div>;
}

/**
 * Waiting when there is nothing else on the screen yet.
 *
 * `Loader` waits *inside* a page — beside a button, or in the content area
 * under a heading that is already true. This is the other situation, and the
 * React app had no answer for it at all: the session check that runs on every
 * page load rendered an empty fragment, so the first thing anybody saw of this
 * product, every single time, was an empty rectangle for the length of a
 * network request. The same fragment covered the two redirects — to sign in,
 * and to the storefront for a path this origin does not serve — where the wait
 * is a whole browser navigation.
 *
 * **`space` is required rather than optional**, which is the one place this
 * differs from `Loader`. Everywhere else the accent resolves from the nearest
 * `[data-gb-space]` ancestor, and there is not one here: this renders *instead*
 * of `Page`, above it, or before it exists. Without the attribute the ground
 * would be the `:root` default — the store's — which is how a studio ends up
 * watching a warm orange loader on the way into its own blue portal.
 *
 * `lg`, because it is alone on the screen and the `md` mark centred in a
 * window reads as something that failed to finish loading rather than as the
 * thing that is loading.
 */
export function PageLoader({
  label,
  space,
  over = false,
}: {
  /** What is being waited for. Announced, so write it for a person. */
  label?: string;
  /** Whose colours. Required: there is no surface above this to inherit from. */
  space: SiteId;
  /**
   * Over the page rather than instead of it.
   *
   * For a navigation, where the page being left is still perfectly good and
   * should stay until the next one is ready. A first load has nothing to cover
   * and takes the default.
   */
  over?: boolean;
}): React.ReactElement {
  return (
    <div
      className={`${styles.loaderPage ?? ''} ${over ? (styles.loaderOver ?? '') : ''}`}
      data-gb-space={space}
    >
      <Loader size="lg" {...(label ? { label } : {})} />
    </div>
  );
}

export interface ScoredVersion {
  version: string;
  /** Whole percent positive. */
  positivePercent: number;
  /** The denominator. A score without one is not shown (section 06). */
  total: number;
}

/**
 * Version history, drawn from real scores.
 *
 * Heights are the scores. There is no smoothing, no minimum delta and no
 * filtering: a title whose score fell draws a descending strip and ships that
 * way, because the guidelines list declining as *"permitted, and required.
 * Honesty is the device."*
 *
 * A single version draws a single bar. A new title is not a staircase.
 */
export function VersionStrip({
  versions,
  size = 'md',
  onDark = false,
}: {
  versions: readonly ScoredVersion[];
  size?: StripSize;
  onDark?: boolean;
}): React.ReactElement | null {
  if (versions.length === 0) return null;

  const label = versions
    .map((entry) => `${entry.version}: ${entry.positivePercent}% from ${entry.total} reviews`)
    .join(', ');

  return (
    <span
      className={`${styles.strip} ${SIZE_CLASS[size]}`}
      role="img"
      aria-label={`Review score by version. ${label}.`}
    >
      {versions.map((entry, index) => (
        <i
          key={entry.version}
          // A 0% version still draws a visible sliver rather than nothing, or
          // the worst version silently disappears from its own history.
          style={{ height: `${Math.max(4, entry.positivePercent)}%` }}
          data-bar={index === versions.length - 1 ? 'current' : 'past'}
          data-on-dark={onDark ? '' : undefined}
        />
      ))}
    </span>
  );
}

/**
 * Download and install progress. Equal bars filling left to right.
 *
 * The fourth state in the guidelines, and the only one where equal heights are
 * correct: nothing here is a score, so nothing should look like one.
 */
export function DownloadStrip({
  total,
  completed,
  size = 'sm',
}: {
  total: number;
  completed: number;
  size?: StripSize;
}): React.ReactElement {
  const bars = Array.from({ length: Math.max(1, total) }, (_, index) => index);

  return (
    <span
      className={`${styles.strip} ${SIZE_CLASS[size]}`}
      role="img"
      aria-label={`${completed} of ${total} complete.`}
    >
      {bars.map((index) => (
        <i
          key={index}
          style={{ height: '100%' }}
          data-bar={index < completed ? 'current' : 'dim'}
        />
      ))}
    </span>
  );
}
